import React, { memo } from "react";

import { Skeleton } from "@/common/components/Skeleton";

const r = (n: number) => Array.from({ length: n }, (_, i) => i);

type HeaderSkeletonProps = {
  titleW: string;
  descW: string;
  showPath?: boolean;
  actions?: React.ReactNode;
};

const PageHeaderSkeleton = ({
  titleW,
  descW,
  showPath = false,
  actions,
}: HeaderSkeletonProps) => (
  <div className="flex items-center justify-between w-full">
    <div className="flex flex-col gap-1">
      {showPath && <Skeleton className="h-3 w-44" />}
      <Skeleton className={`h-6 ${titleW}`} />
      <Skeleton className={`h-3.5 ${descW}`} />
    </div>
    {actions ? <div className="flex items-center gap-3">{actions}</div> : null}
  </div>
);

const TableSurfaceSkeleton = ({
  colCount = 5,
  rowCount = 10,
  withToolbar = true,
  withOuterPadding = false,
}: {
  colCount?: number;
  rowCount?: number;
  withToolbar?: boolean;
  withOuterPadding?: boolean;
}) => (
  <div className="app-card flex-1 overflow-hidden">
    <div className={withOuterPadding ? "p-4 h-full" : "h-full"}>
      {withToolbar ? (
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
          <Skeleton className="h-8 flex-1 max-w-xs rounded-md" />
          <Skeleton className="h-7 w-20 rounded-md" />
          <Skeleton className="h-7 w-20 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      ) : null}

      <div className="border-b border-border bg-muted/30">
        <div className="flex items-center gap-4 px-4 py-2.5">
          <Skeleton className="h-4 w-4 rounded-sm" />
          {r(colCount).map((i) => (
            <Skeleton key={i} className="h-3.5 flex-1" />
          ))}
        </div>
      </div>

      {r(rowCount).map((i) => (
        <div
          key={i}
          className="flex items-center gap-4 px-4 py-3 border-b border-border/60 last:border-b-0"
        >
          <Skeleton className="h-4 w-4 rounded-sm" />
          {r(colCount).map((j) => (
            <Skeleton key={j} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   Shared building blocks — reused by the full-page skeleton AND by the
   individual panels (folder tree, test steps) so a standalone load shows the
   exact same structure as the corresponding slice of the full-page skeleton.
   ───────────────────────────────────────────────────────── */

/**
 * Folder-tree rows skeleton. Mirrors FolderView's tree row:
 * chevron + folder icon + name + count, indented by `depth * 16 + 12`.
 * Renders rows only (no outer padding/scroll) so callers control the container.
 */
export const FolderTreeSkeleton = memo(({ rows = 8 }: { rows?: number }) => (
  <div className="flex flex-col">
    {r(rows).map((i) => {
      const depth = i % 3;
      return (
        <div
          key={i}
          className="flex items-center gap-1.5 py-1.5 mb-1"
          style={{ paddingLeft: `${depth * 16 + 12}px`, paddingRight: "12px" }}
        >
          <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
          <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
          <Skeleton
            className="h-3.5 flex-1"
            style={{ maxWidth: `${90 + ((i * 17) % 70)}px` }}
          />
          <Skeleton className="h-3 w-4 rounded flex-shrink-0" />
        </div>
      );
    })}
  </div>
));

/**
 * Test-steps rows skeleton. Mirrors a step row: drag handle + step number +
 * step text. Renders rows only so callers control the surrounding container.
 */
export const TestStepsSkeleton = memo(
  ({
    rows = 6,
    withAddRow = true,
  }: {
    rows?: number;
    withAddRow?: boolean;
  }) => (
    <div className="flex flex-col">
      {r(rows).map((i) => (
        <div key={i} className="flex items-center gap-4 px-4 py-3">
          <Skeleton className="h-5 w-6 rounded flex-shrink-0" />
          <Skeleton className="h-4 w-8 rounded flex-shrink-0" />
          <Skeleton
            className="h-4 flex-1"
            style={{ maxWidth: `${50 + ((i * 13) % 40)}%` }}
          />
        </div>
      ))}
      {withAddRow && (
        <div className="flex items-center gap-3 px-4 py-3 mt-1">
          <Skeleton className="h-5 w-5 rounded" />
          <Skeleton className="h-4 w-24" />
        </div>
      )}
    </div>
  ),
);

/**
 * Left tree sidebar skeleton — mirrors FolderView (web/) exactly: a 280px
 * full-height column with an h-14 header (icon + "Test Cases" + 2 action
 * buttons), a search bar, the script-type filter pills, the status filter row,
 * and the folder tree. Used by both the test-cases list and detail skeletons.
 */
const TestCasesTreeSidebarSkeleton = () => (
  <div className="flex w-[280px] shrink-0 flex-col overflow-hidden border-r border-border bg-background">
    {/* h-14 header: icon + title + action buttons */}
    <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
      <Skeleton className="h-7 w-7 rounded-md" />
      <Skeleton className="h-4 w-20" />
      <div className="ml-auto flex items-center gap-1.5">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
    </div>
    {/* Search bar */}
    <div className="shrink-0 border-b border-border px-3 py-2.5">
      <Skeleton className="h-8 w-full rounded-md" />
    </div>
    {/* Script-type filter pills */}
    <div className="flex shrink-0 gap-1.5 border-b border-border px-3 pb-2 pt-2">
      <Skeleton className="h-5 w-12 rounded-md" />
      <Skeleton className="h-5 w-20 rounded-md" />
      <Skeleton className="h-5 w-16 rounded-md" />
    </div>
    {/* Status filter row */}
    <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2">
      <Skeleton className="h-3 w-8" />
      <Skeleton className="h-3 w-12" />
      <Skeleton className="h-3 w-12" />
      <Skeleton className="h-3 w-10" />
      <Skeleton className="ml-auto h-3.5 w-3.5 rounded" />
    </div>
    {/* Folder tree */}
    <div className="min-h-0 flex-1 overflow-hidden py-2">
      <FolderTreeSkeleton rows={9} />
    </div>
  </div>
);

/* ─────────────────────────────────────────────────────────
   Test Cases — list page  (/testcases/all|automation|manual|performance)
   Layout: [Tree sidebar 280px | empty "Select a test case" center]
   ───────────────────────────────────────────────────────── */
export const TestCasesListSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full flex-row bg-background">
    <TestCasesTreeSidebarSkeleton />
    {/* Center — mirrors the white "Select a test case" empty state */}
    <div className="flex flex-1 flex-col overflow-y-auto scrollbar-thin bg-gradient-to-br from-[#f8fafc] to-[#eef2f7] dark:from-background dark:to-[#0d0f14]">
      <div className="mx-auto w-[80%] max-w-5xl py-8 flex flex-col gap-6">
        {/* ───── Hero Skeleton ───── */}
        <section className="flex flex-col items-center text-center">
          <div className="relative mx-auto mb-6 w-full max-w-[550px] h-[280px] flex items-center justify-between px-4 sm:px-8">
            {/* Connecting Lines */}
            <svg className="absolute inset-0 w-full h-full text-muted-foreground/20 z-0 pointer-events-none">
              <line
                x1="25%"
                y1="30%"
                x2="40%"
                y2="45%"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="5 5"
              />
              <line
                x1="25%"
                y1="70%"
                x2="40%"
                y2="55%"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="5 5"
              />
              <line
                x1="75%"
                y1="30%"
                x2="60%"
                y2="45%"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="5 5"
              />
              <line
                x1="75%"
                y1="70%"
                x2="60%"
                y2="55%"
                stroke="currentColor"
                strokeWidth="2"
                strokeDasharray="5 5"
              />
            </svg>

            {/* Left Column */}
            <div className="flex flex-col gap-10 relative z-10">
              <div className="flex flex-col items-center gap-2.5">
                <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-muted-foreground/15" />
                <Skeleton className="h-2 w-16 bg-muted-foreground/15" />
                <Skeleton className="h-2 w-12 bg-muted-foreground/15" />
              </div>
              <div className="flex flex-col items-center gap-2.5">
                <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-muted-foreground/15" />
                <Skeleton className="h-2 w-16 bg-muted-foreground/15" />
                <Skeleton className="h-2 w-12 bg-muted-foreground/15" />
              </div>
            </div>

            {/* Center Checklist */}
            <Skeleton className="relative h-[150px] w-[180px] sm:w-[220px] rounded-2xl flex flex-col justify-center items-center gap-4 p-5 shadow-sm z-10 bg-muted-foreground/10 border border-muted-foreground/10">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 w-full px-1">
                  <Skeleton className="h-5 w-5 sm:h-6 sm:w-6 rounded-md bg-muted-foreground/20" />
                  <Skeleton className="h-2.5 flex-1 bg-muted-foreground/20" />
                </div>
              ))}
            </Skeleton>

            {/* Right Column */}
            <div className="flex flex-col gap-10 relative z-10">
              <div className="flex flex-col items-center gap-2.5">
                <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-muted-foreground/15" />
                <Skeleton className="h-2 w-16 bg-muted-foreground/15" />
                <Skeleton className="h-2 w-12 bg-muted-foreground/15" />
              </div>
              <div className="flex flex-col items-center gap-2.5">
                <Skeleton className="h-14 w-14 sm:h-16 sm:w-16 rounded-2xl bg-muted-foreground/15" />
                <Skeleton className="h-2 w-16 bg-muted-foreground/15" />
                <Skeleton className="h-2 w-12 bg-muted-foreground/15" />
              </div>
            </div>
          </div>

          <Skeleton className="mt-4 h-5 w-64 rounded-md" />
          <Skeleton className="mt-2.5 h-4 w-96 max-w-md rounded-md" />
          <Skeleton className="mt-1 h-4 w-72 max-w-sm rounded-md" />

          {/* CTAs */}
          <div className="mt-6 flex items-center gap-2">
            <Skeleton className="h-9 w-36 rounded-md" />
            <Skeleton className="h-9 w-40 rounded-md" />
          </div>
        </section>


        {/* ───── Quick Actions Skeleton ───── */}
        <section>
          <Skeleton className="h-4 w-28 rounded-md" />
          <Skeleton className="mt-1.5 h-3 w-64 rounded-md" />

          <div className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex flex-col rounded-lg border border-border p-4 bg-background"
              >
                <Skeleton className="h-8 w-8 rounded-md" />
                <Skeleton className="mt-3 h-4 w-32 rounded-md" />
                <Skeleton className="mt-2 h-3 w-full rounded-md" />
                <Skeleton className="mt-1 h-3 w-4/5 rounded-md" />
              </div>
            ))}
          </div>
        </section>

        {/* ───── Statistics Skeleton ───── */}
        <section>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="flex items-center gap-3 rounded-lg border border-border p-4 bg-background"
              >
                <Skeleton className="h-8 w-8 rounded-md shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <Skeleton className="h-5 w-12 rounded-md" />
                  <Skeleton className="h-3 w-24 rounded-md" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   Cycles page  (/cycles, /cycles?id=:id)
   Layout: [Release tree 280px | Cycle workspace | Context panel 320px]
   Mirrors CyclesPage → ReleaseTree + CycleWorkspace + CycleContext so the
   bootstrap skeleton matches the real 3-pane cycle layout 1:1.
   ───────────────────────────────────────────────────────── */

/** Left release/cycle tree (w-[280px]) — header + ⌘K search + tree rows. */
const CyclesTreeSidebarSkeleton = () => (
  <div className="flex w-[280px] shrink-0 flex-col overflow-hidden border-r border-border bg-background">
    {/* h-14 header: avatar + "Cycles" title + New cycle button */}
    <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
      <Skeleton className="h-7 w-7 rounded-md" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="ml-auto h-7 w-20 rounded-md" />
    </div>
    {/* Search bar */}
    <div className="shrink-0 border-b border-border px-3 py-2.5">
      <Skeleton className="h-8 w-full rounded-md" />
    </div>
    {/* Release / cycle tree */}
    <div className="min-h-0 flex-1 overflow-hidden py-2">
      <FolderTreeSkeleton rows={9} />
    </div>
  </div>
);

/** Right cycle context panel (w-[320px], xl+) — Details/Activity + sections. */
const CycleContextSkeleton = () => (
  <aside className="hidden w-[320px] shrink-0 flex-col border-l border-border bg-background xl:flex">
    {/* h-14 top tabs: Details | Activity */}
    <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-2">
      <Skeleton className="h-7 w-20 rounded-md" />
      <Skeleton className="h-7 w-20 rounded-md" />
    </div>
    <div className="flex flex-col gap-5 p-5">
      {/* CY-id + type badge */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-5 w-24 rounded-md" />
      </div>
      {/* WINDOW | PASS RATE | CASES inline meta */}
      <div className="flex items-end justify-between">
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-14" />
          <Skeleton className="h-4 w-24" />
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <Skeleton className="h-2.5 w-14" />
          <Skeleton className="h-4 w-8" />
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <Skeleton className="h-2.5 w-10" />
          <Skeleton className="h-4 w-8" />
        </div>
      </div>
      {/* STATUS */}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-2.5 w-14" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
      {/* NEXT SCHEDULED RUN */}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-2.5 w-32" />
        <Skeleton className="h-12 w-full rounded-md" />
      </div>
      {/* HEALTH — 3 stat boxes */}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-2.5 w-16" />
        <div className="grid grid-cols-3 gap-2">
          <Skeleton className="h-12 rounded-md" />
          <Skeleton className="h-12 rounded-md" />
          <Skeleton className="h-12 rounded-md" />
        </div>
      </div>
      {/* SUMMARY — label + value rows */}
      <div className="flex flex-col gap-2">
        <Skeleton className="h-2.5 w-20" />
        {r(3).map((i) => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-3.5 w-24" />
            <Skeleton className="h-3.5 w-6" />
          </div>
        ))}
      </div>
      {/* ATTENTION callout */}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-2.5 w-20" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>
    </div>
  </aside>
);

export const CyclesPageSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full flex-row bg-background">
    <CyclesTreeSidebarSkeleton />

    {/* Center: header + execution-progress meta row + tabs + Cycle health cards */}
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
      {/* h-14 header: calendar + breadcrumb (release › CY-id › name) + actions */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-6">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-4 w-40" />
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-7 w-24 rounded-md" />
          <Skeleton className="h-7 w-24 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>

      {/* Execution-progress meta row */}
      <div className="flex items-center gap-6 border-b border-border bg-muted/30 px-6 py-3">
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-center justify-between">
            <Skeleton className="h-2.5 w-36" />
            <Skeleton className="h-4 w-10" />
          </div>
          <Skeleton className="h-2 w-full rounded-full" />
          <div className="flex items-center gap-4">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-20" />
          </div>
        </div>
        <div className="h-10 w-px shrink-0 bg-border" />
        <div className="flex shrink-0 flex-col gap-1.5">
          <Skeleton className="h-2.5 w-14" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="h-10 w-px shrink-0 bg-border" />
        <div className="flex shrink-0 flex-col items-center gap-1.5">
          <Skeleton className="h-2.5 w-14" />
          <Skeleton className="h-5 w-10" />
        </div>
        <div className="flex shrink-0 flex-col items-center gap-1.5">
          <Skeleton className="h-2.5 w-10" />
          <Skeleton className="h-5 w-8" />
        </div>
      </div>

      {/* Tab bar (Overview, Scope, Configurations, Schedules, Execution, Reports, Defects) */}
      <div className="flex h-10 shrink-0 items-center gap-6 border-b border-border px-4">
        {[14, 12, 24, 18, 16, 14, 12].map((w, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Skeleton className="h-3.5 w-3.5 rounded" />
            <Skeleton className="h-3.5" style={{ width: `${w * 4}px` }} />
          </div>
        ))}
      </div>

      {/* Overview content: Cycle health label + 8 metric cards */}
      <div className="flex-1 overflow-hidden px-6 py-5">
        <Skeleton className="mb-3 h-2.5 w-24" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {r(8).map((i) => (
            <div
              key={i}
              className="rounded-lg border border-border bg-background p-4"
            >
              <Skeleton className="h-6 w-12" />
              <Skeleton className="mt-2 h-2.5 w-16" />
            </div>
          ))}
        </div>
      </div>
    </div>

    <CycleContextSkeleton />
  </div>
));

/* ─────────────────────────────────────────────────────────
   Test Cases — detail page  (/testcases/:id, /testcases/:id/testpage/:type)
   Layout: [Tree sidebar 280px | Steps workspace | Context panel 320px]
   ───────────────────────────────────────────────────────── */
export const TestCaseDetailSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full flex-row bg-background">
    <TestCasesTreeSidebarSkeleton />

    {/* Center: panel header + tags + workspace tabs + steps + AI bar */}
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
      {/* h-14 panel header: TC-id + version + › + title + actions */}
      <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-6">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-4 w-9 rounded-full" />
        <Skeleton className="h-3 w-3 rounded" />
        <Skeleton className="h-4 w-52" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-16 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
      </div>
      {/* Tag/meta row */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-6 py-2.5">
        <Skeleton className="h-5 w-16 rounded-md" />
        <Skeleton className="h-5 w-28 rounded-md" />
      </div>

      {/* Workspace: tab bar + steps + bottom AI bar */}
      <div className="flex min-h-0 flex-1 flex-col px-4">
        {/* tab bar (Steps | Test Cycles | Trial Run History | Defects) — h-[50px] gap-6 */}
        <div className="flex h-[50px] shrink-0 items-center gap-6 border-b border-border">
          <Skeleton className="h-3.5 w-10" />
          <Skeleton className="h-3.5 w-16" />
          <Skeleton className="h-3.5 w-18" />
          <Skeleton className="h-3.5 w-12" />
        </div>
        {/* numbered steps */}
        <div className="flex-1 pt-3">
          <TestStepsSkeleton rows={7} />
        </div>
        {/* bottom AI bar */}
        <div className="py-3">
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
    </div>

    {/* Right context panel (320px) */}
    <aside className="hidden w-[320px] shrink-0 flex-col border-l border-border bg-background xl:flex">
      {/* top tabs: Details | Version | Sample */}
      <div className="flex shrink-0 items-center gap-2 border-b border-border px-3 py-2.5">
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-4 w-14" />
        <Skeleton className="h-4 w-14" />
      </div>
      <div className="flex flex-col gap-4 p-5">
        {/* TC id + Automation badge */}
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-5 w-24 rounded-md" />
        </div>
        {/* STATUS field */}
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-14" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        {/* REVIEWER field */}
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-16" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        {/* TEST CYCLES */}
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-20" />
          <div className="flex gap-1.5">
            <Skeleton className="h-6 w-16 rounded-md" />
            <Skeleton className="h-6 w-20 rounded-md" />
          </div>
        </div>
        {/* REQUIREMENTS */}
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-24" />
          <div className="flex gap-1.5">
            <Skeleton className="h-6 w-20 rounded-md" />
            <Skeleton className="h-6 w-24 rounded-md" />
          </div>
        </div>
        {/* EXECUTION HEALTH — 3 stat boxes */}
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-28" />
          <div className="grid grid-cols-3 gap-2">
            <Skeleton className="h-12 rounded-md" />
            <Skeleton className="h-12 rounded-md" />
            <Skeleton className="h-12 rounded-md" />
          </div>
        </div>
        {/* RECENT RUNS */}
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-20" />
          <Skeleton className="h-10 w-full rounded-md" />
        </div>
      </div>
    </aside>
  </div>
));

export const LoadTestExecutionDetailSkeleton = memo(() => (
  <div className="space-y-3 p-3">
    <div className="rounded-lg border border-border bg-card">
      <div className="border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-5 w-20 rounded-full" />
          <Skeleton className="h-5 w-14 rounded-full" />
          <div className="ml-auto flex items-center gap-1.5">
            <Skeleton className="h-7 w-16 rounded-md" />
            <Skeleton className="h-7 w-16 rounded-md" />
            <Skeleton className="h-7 w-16 rounded-md" />
          </div>
        </div>
        <Skeleton className="mt-2 h-3 w-80 max-w-full" />
      </div>
      <div className="flex items-center gap-3 border-b border-border px-4 py-2.5">
        {r(6).map((i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Skeleton className="h-3.5 w-14" />
            {i % 2 === 0 ? <Skeleton className="h-4 w-5 rounded-full" /> : null}
          </div>
        ))}
      </div>
      <div className="space-y-3 p-4">
        <div className="grid gap-3 md:grid-cols-4">
          {r(4).map((i) => (
            <div
              key={i}
              className="rounded-md border border-border bg-muted/20 p-3"
            >
              <Skeleton className="h-3 w-20" />
              <Skeleton className="mt-2 h-6 w-16" />
              <Skeleton className="mt-2 h-3 w-24" />
            </div>
          ))}
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <div className="rounded-md border border-border bg-card p-3">
            <Skeleton className="h-4 w-32" />
            <div className="mt-3 space-y-2">
              {r(8).map((i) => (
                <Skeleton key={i} className="h-3 w-full" />
              ))}
            </div>
          </div>
          <div className="rounded-md border border-border bg-card p-3">
            <Skeleton className="h-4 w-36" />
            <div className="mt-3 space-y-2">
              {r(8).map((i) => (
                <Skeleton key={i} className="h-3 w-full" />
              ))}
            </div>
          </div>
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <Skeleton className="h-40 rounded-md" />
          <Skeleton className="h-40 rounded-md" />
        </div>
      </div>
    </div>
  </div>
));

export const LoadTestExecutionWorkspaceSkeleton = memo(() => (
  <section className="flex h-full min-w-0 flex-col bg-background">
    <header className="shrink-0 border-b border-border bg-card px-4 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[240px] flex-1 basis-[280px]">
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-44" />
            <Skeleton className="h-5 w-20 rounded-full" />
            <Skeleton className="h-5 w-14 rounded-full" />
          </div>
          <Skeleton className="mt-2 h-3 w-80 max-w-full" />
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-7 w-16 rounded-md" />
          <Skeleton className="h-7 w-16 rounded-md" />
          <Skeleton className="h-7 w-16 rounded-md" />
        </div>
      </div>
      <div className="mt-2 flex items-center gap-3">
        {r(7).map((i) => (
          <div key={i} className="flex items-center gap-1.5">
            <Skeleton className="h-4 w-14" />
            {i % 2 === 0 ? <Skeleton className="h-4 w-5 rounded-full" /> : null}
          </div>
        ))}
      </div>
    </header>

    <div className="min-h-0 flex-1 overflow-y-auto p-2.5">
      <div className="grid gap-2.5 lg:grid-cols-[minmax(0,1fr)_460px]">
        <div className="space-y-2.5">
          <div className="rounded-md border border-border bg-card p-3">
            <Skeleton className="mb-2 h-4 w-36" />
            <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4">
              {r(12).map((i) => (
                <div
                  key={i}
                  className="rounded-lg border border-border bg-card p-3"
                >
                  <div className="flex items-start gap-2.5">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Skeleton className="h-2.5 w-24" />
                      <Skeleton className="h-6 w-20" />
                      <Skeleton className="h-2.5 w-12" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-lg border border-border bg-card p-3">
            <Skeleton className="h-4 w-32" />
            <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-4">
              <Skeleton className="h-18 w-full rounded-md" />
              <Skeleton className="h-18 w-full rounded-md" />
              <Skeleton className="h-18 w-full rounded-md" />
              <Skeleton className="h-18 w-full rounded-md" />
            </div>
            <div className="mt-3 flex gap-1.5">
              {r(5).map((i) => (
                <Skeleton key={i} className="h-6 w-20 rounded-full" />
              ))}
            </div>
            <Skeleton className="mt-3 h-[280px] w-full rounded-md" />
          </div>

          <div className="grid gap-2.5 xl:grid-cols-2">
            <Skeleton className="h-[220px] rounded-md" />
            <Skeleton className="h-[220px] rounded-md" />
            <Skeleton className="h-[220px] rounded-md" />
            <Skeleton className="h-[220px] rounded-md" />
          </div>
        </div>

        <div className="space-y-2.5">
          <div className="rounded-md border border-border bg-card p-3">
            <Skeleton className="mb-2 h-4 w-24" />
            <Skeleton className="mb-2 h-7 w-full rounded-md" />
            <div className="space-y-2">
              {r(12).map((i) => (
                <Skeleton key={i} className="h-3 w-full" />
              ))}
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-3">
            <Skeleton className="mb-2 h-4 w-28" />
            <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2">
              {r(8).map((i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="h-2.5 w-16" />
                  <Skeleton className="h-3.5 w-28" />
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-md border border-border bg-card p-3">
            <Skeleton className="mb-2 h-4 w-20" />
            <div className="grid grid-cols-1 gap-x-3 gap-y-2.5 sm:grid-cols-2">
              {r(6).map((i) => (
                <div key={i} className="space-y-1">
                  <Skeleton className="h-2.5 w-14" />
                  <Skeleton className="h-3.5 w-24" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
));

export const LoadTestExecutionInspectorSkeleton = memo(() => (
  <div className="flex h-full min-w-0 flex-col border-l border-border bg-surface">
    <div className="shrink-0 border-b border-border px-3 py-2.5">
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-16" />
        <Skeleton className="ml-auto h-5 w-20 rounded-full" />
      </div>
    </div>

    <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
      <div className="rounded-lg border border-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between">
          <Skeleton className="h-3.5 w-16" />
          <Skeleton className="h-3 w-14" />
        </div>
        <Skeleton className="h-2 w-full rounded-full" />
        <div className="mt-2 flex items-center justify-between">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-3 w-14" />
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between">
          <Skeleton className="h-3.5 w-14" />
          <Skeleton className="h-3 w-20" />
        </div>
        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {r(8).map((i) => (
            <div key={i}>
              <Skeleton className="h-2.5 w-12" />
              <Skeleton className="mt-1.5 h-3.5 w-16" />
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-border bg-card p-3">
        <div className="mb-2 flex items-center justify-between">
          <Skeleton className="h-3.5 w-16" />
          <Skeleton className="h-3 w-24" />
        </div>
        <div className="space-y-2">
          {r(6).map((i) => (
            <div key={i}>
              <Skeleton className="h-2.5 w-12" />
              <Skeleton className="mt-1.5 h-3.5 w-24" />
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
));

type LiveMonitoringSkeletonTab =
  | "overview"
  | "performance"
  | "requests"
  | "errors"
  | "logs"
  | "more";

const MonitoringMainPanelSkeleton = ({
  rows = 7,
  withTable = false,
}: {
  rows?: number;
  withTable?: boolean;
}) => (
  <div className="space-y-3">
    <Skeleton className="h-72 w-full rounded-md" />
    <div className="grid gap-3 lg:grid-cols-2">
      <Skeleton className="h-44 w-full rounded-md" />
      <Skeleton className="h-44 w-full rounded-md" />
    </div>
    {withTable ? (
      <div className="rounded-md border border-border bg-card">
        <div className="border-b border-border px-3 py-2">
          <Skeleton className="h-3.5 w-40" />
        </div>
        <div className="space-y-2 p-3">
          {r(rows).map((i) => (
            <div key={i} className="grid grid-cols-12 items-center gap-2">
              <Skeleton className="col-span-4 h-3" />
              <Skeleton className="col-span-3 h-3" />
              <Skeleton className="col-span-3 h-3" />
              <Skeleton className="col-span-2 h-3" />
            </div>
          ))}
        </div>
      </div>
    ) : null}
  </div>
);

export const LiveMonitoringTabSkeleton = memo(
  ({ tab }: { tab: LiveMonitoringSkeletonTab }) => {
    if (tab === "overview" || tab === "performance") {
      return (
        <div className="space-y-2.5">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <Skeleton className="h-40 rounded-md lg:row-span-2" />
            {r(8).map((i) => (
              <Skeleton key={i} className="h-20 rounded-md" />
            ))}
          </div>
          <div className="grid gap-2.5 xl:grid-cols-[minmax(0,1fr)_320px]">
            <div className="space-y-2.5">
              <Skeleton className="h-64 rounded-md" />
              <div className="grid gap-2.5 md:grid-cols-2">
                <Skeleton className="h-44 rounded-md" />
                <Skeleton className="h-44 rounded-md" />
              </div>
            </div>
            <Skeleton className="h-[28.5rem] rounded-md" />
          </div>
        </div>
      );
    }

    if (tab === "requests" || tab === "errors") {
      return <MonitoringMainPanelSkeleton withTable rows={8} />;
    }

    if (tab === "logs") {
      return <MonitoringMainPanelSkeleton withTable rows={10} />;
    }

    if (tab === "more") {
      return (
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <Skeleton className="h-10 rounded-md md:col-span-2" />
            <Skeleton className="h-10 rounded-md" />
          </div>
          <Skeleton className="h-80 rounded-md" />
          <div className="grid gap-3 lg:grid-cols-3">
            <Skeleton className="h-36 rounded-md" />
            <Skeleton className="h-36 rounded-md" />
            <Skeleton className="h-36 rounded-md" />
          </div>
        </div>
      );
    }

    return <MonitoringMainPanelSkeleton />;
  },
);

/* ─────────────────────────────────────────────────────────
   1. All Scenarios  (/testcases/automation, /manual, /performance)
   Layout: Breadcrumb + [Folder sidebar 360px | Table with tabs]
   ───────────────────────────────────────────────────────── */
export const SidebarTablePageSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton
      showPath
      titleW="w-56"
      descW="w-72"
      actions={
        <>
          <Skeleton className="h-9 w-32 rounded-md" />
          <Skeleton className="h-9 w-36 rounded-md" />
        </>
      }
    />

    {/* Main: sidebar + table */}
    <div className="flex h-full min-h-0 flex-row gap-4">
      {/* Folder sidebar — matches FolderView: header border-b, "Test Folder" + add button, then tree items */}
      <div className="app-card w-[360px] shrink-0 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <Skeleton className="h-5 w-24" />
          <Skeleton className="h-8 w-8 rounded-md" />
        </div>
        {/* Folder tree items */}
        <div className="p-2">
          <FolderTreeSkeleton rows={8} />
        </div>
      </div>

      {/* Right panel — matches TestCases: tabs + search/filter bar + table rows */}
      <div className="app-card flex-1 overflow-hidden p-4 flex flex-col gap-4">
        {/* Tabs (Test Cases | Test Step Groups) */}
        <div className="flex gap-2 items-center">
          <Skeleton className="h-9 w-32 rounded-md" />
          <Skeleton className="h-9 w-40 rounded-md" />
        </div>

        {/* Search + filter row */}
        <div className="flex items-center justify-between w-full gap-4">
          <Skeleton className="h-9 flex-1 rounded-md" />
          <div className="flex gap-2 items-center">
            <Skeleton className="h-7 w-24 rounded-md" />
            <Skeleton className="h-7 w-20 rounded-md" />
          </div>
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>

        {/* Table */}
        <div className="flex-1 rounded-md border border-border overflow-hidden">
          {/* Table header */}
          <div className="bg-muted/30 border-b border-border">
            <div className="flex items-center gap-4 px-4 py-2.5">
              <Skeleton className="h-4 w-4 rounded-sm" />
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-3.5 flex-1" />
              <Skeleton className="h-3.5 w-20" />
              <Skeleton className="h-3.5 w-16" />
            </div>
          </div>
          {/* Table rows */}
          {r(10).map((i) => (
            <div key={i} className="border-b border-border/60 last:border-b-0">
              <div className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-4 w-4 rounded-sm" />
                <Skeleton className="h-4 w-16" />
                <Skeleton
                  className="h-4 flex-1"
                  style={{ maxWidth: `${50 + ((i * 7) % 40)}%` }}
                />
                <Skeleton className="h-4 w-10" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   2. Command Center Dashboard  (/dashboard)
   Layout: [w-64 sidebar | main: h-14 header + release banner + KPI grid
            + burndown(2col)/forecast + sub-stat cards]
   Mirrors CommandCenterPage shell + PmDashboard body so the skeleton matches
   the real structure (not a generic table grid).
   ───────────────────────────────────────────────────────── */
export const CommandCenterSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full overflow-hidden bg-background dark:bg-gradient-to-br dark:from-[#27272a] dark:to-[#09090b]">
    {/* Sidebar — Dashboard title, search, saved views, filters */}
    <aside className="no-scrollbar flex w-[280px] shrink-0 flex-col overflow-hidden border-r border-border bg-background dark:bg-transparent">
      <div className="flex h-14 items-center border-b border-border px-4">
        <Skeleton className="h-4 w-24" />
      </div>
      <div className="border-b border-border px-3 py-2">
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
      {/* Saved views */}
      <div className="border-b border-border px-3 py-2.5">
        <Skeleton className="mb-2 ml-2 h-2.5 w-20" />
        <div className="space-y-1.5">
          {r(5).map((i) => (
            <div key={i} className="flex items-center gap-2 px-2">
              <Skeleton className="h-3 w-3 rounded" />
              <Skeleton className="h-3.5 flex-1" />
            </div>
          ))}
        </div>
      </div>
      {/* Filters */}
      <div className="px-3 py-2.5">
        <Skeleton className="mb-2 ml-2 h-2.5 w-14" />
        <div className="space-y-3">
          {r(3).map((i) => (
            <div key={i}>
              <Skeleton className="mb-1.5 h-2.5 w-16" />
              <Skeleton className="h-8 w-full rounded-md" />
            </div>
          ))}
        </div>
      </div>
    </aside>

    {/* Main */}
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-[#F8FAFC] dark:bg-transparent">
      {/* h-14 header — breadcrumb + title + 3 actions */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border bg-background dark:bg-transparent px-6">
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-2.5 w-40" />
          <Skeleton className="h-4 w-56" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-20 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </div>

      {/* Content */}
      <div className="min-h-0 flex-1 overflow-hidden px-6 py-5">
        <div className="space-y-4">
          {/* Release banner — label/name left, 3 counters right */}
          <div className="rounded-lg border border-border bg-card dark:bg-transparent px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex flex-col gap-1.5">
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="h-4 w-44" />
                <Skeleton className="h-2.5 w-28" />
              </div>
              <div className="flex items-center gap-5">
                {r(3).map((i) => (
                  <React.Fragment key={i}>
                    {i > 0 && <div className="h-8 w-px bg-border" />}
                    <div className="flex flex-col items-center gap-1.5">
                      <Skeleton className="h-5 w-8" />
                      <Skeleton className="h-2.5 w-16" />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>

          {/* KPI cards — grid-cols-4 xl:grid-cols-8 */}
          <div className="grid grid-cols-4 gap-3 xl:grid-cols-8">
            {r(8).map((i) => (
              <div
                key={i}
                className="rounded-lg border border-border bg-card dark:bg-transparent p-3"
              >
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="mt-2 h-6 w-12" />
                <Skeleton className="mt-2 h-2.5 w-20" />
              </div>
            ))}
          </div>

          {/* Burndown (2/3) + Ship forecast side panel (1/3) */}
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2 rounded-lg border border-border bg-card dark:bg-transparent p-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="mt-1 h-2.5 w-36" />
              <Skeleton className="mt-4 h-44 w-full rounded-md" />
              <div className="mt-4 flex items-center justify-center gap-3">
                <Skeleton className="h-8 w-20 rounded-full" />
                <Skeleton className="h-8 w-20 rounded-full" />
              </div>
              <div className="mt-4 grid grid-cols-4 gap-3">
                {r(4).map((i) => (
                  <div
                    key={i}
                    className="rounded-lg border border-border bg-card dark:bg-transparent p-3"
                  >
                    <Skeleton className="h-2.5 w-14" />
                    <Skeleton className="mt-2 h-5 w-10" />
                    <Skeleton className="mt-2 h-2.5 w-16" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-border bg-card dark:bg-transparent p-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="mt-1 h-2.5 w-32" />
              <Skeleton className="mt-4 h-24 w-full rounded-md" />
              <Skeleton className="mt-4 h-2.5 w-40" />
              <Skeleton className="mt-3 h-8 w-full rounded-md" />
            </div>
          </div>

          {/* Secondary stat cards row */}
          <div className="grid grid-cols-4 gap-3">
            {r(4).map((i) => (
              <div
                key={i}
                className="rounded-lg border border-border bg-card dark:bg-transparent p-4"
              >
                <Skeleton className="h-2.5 w-16" />
                <Skeleton className="mt-2 h-6 w-14" />
                <Skeleton className="mt-2 h-2.5 w-20" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   2. Dashboard  (/)
   Layout: Breadcrumb + TimelineFilter + 5 StatsCards + GraphCharts grid
   ───────────────────────────────────────────────────────── */
export const DashboardSkeleton = memo(() => (
  <div className="w-full flex flex-col gap-3 pb-6 py-4">
    {/* Breadcrumb */}
    <div className="flex flex-col gap-1">
      <Skeleton className="h-6 w-80" />
      <Skeleton className="h-3.5 w-[500px]" />
    </div>

    {/* TimelineFilter: label left + segmented control right */}
    <div className="flex items-center justify-between mt-2 px-4 py-2.5 my-1 bg-background rounded-md">
      <Skeleton className="h-3 w-36" />
      <Skeleton className="h-8 w-80 rounded-md" />
    </div>

    {/* 5 Stats cards — grid-cols-5 */}
    <div className="w-full grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
      {r(5).map((i) => (
        <div
          key={i}
          className="dashboard-card bg-card px-4 py-3 rounded-md flex flex-col gap-1.5"
        >
          <Skeleton className="h-3.5 w-28" />
          <Skeleton className="h-7 w-12" />
        </div>
      ))}
    </div>

    {/* Charts: 3-col grid */}
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
      {/* Left 2/3 */}
      <div className="xl:col-span-2 flex flex-col gap-3">
        {/* Top row: 2 chart cards side by side */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Active Test Runs — doughnut + indicators */}
          <div className="dashboard-card bg-card p-4 rounded-md h-full flex flex-col">
            <Skeleton className="h-4 w-36 mb-3" />
            <div className="flex items-center justify-between flex-1">
              <div className="flex flex-col gap-2">
                {r(4).map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Skeleton className="h-3 w-3 rounded-full" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-36 w-36 rounded-full" />
            </div>
          </div>
          {/* Recently Failed — list items */}
          <div className="dashboard-card bg-card p-4 rounded-md h-full flex flex-col">
            <Skeleton className="h-4 w-44 mb-4" />
            <div className="flex flex-col gap-3">
              {r(4).map((i) => (
                <div
                  key={i}
                  className="bg-muted/30 rounded-md py-2.5 px-3.5 flex items-center justify-between"
                >
                  <div className="flex flex-col gap-1">
                    <Skeleton className="h-3.5 w-40" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* LatestRun — 4 mini stat cards */}
        <div className="w-full grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          {r(4).map((i) => (
            <div
              key={i}
              className="dashboard-card bg-card px-4 py-3 rounded-md flex flex-col gap-1.5"
            >
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-7 w-8" />
            </div>
          ))}
        </div>
      </div>

      {/* Right 1/3 — Test Plan Schedule */}
      <div className="dashboard-card bg-card p-4 rounded-md h-full flex flex-col">
        <Skeleton className="h-4 w-40 mb-3" />
        <div className="flex items-center justify-between mb-3">
          <Skeleton className="h-7 w-7 rounded" />
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-7 w-7 rounded" />
        </div>
        <div className="flex flex-col gap-2 flex-1">
          {r(6).map((i) => (
            <Skeleton key={i} className="h-8 w-full rounded-md" />
          ))}
        </div>
      </div>
    </div>

    {/* Bottom row: 3 chart cards */}
    <div className="grid grid-cols-1 xl:grid-cols-3 gap-3">
      {/* Browser Usage — pie chart */}
      <div className="dashboard-card bg-card p-4 rounded-md flex flex-col min-h-[300px]">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-40 w-40 rounded-full" />
        </div>
      </div>
      {/* Test Case Summary — doughnut + chips */}
      <div className="dashboard-card bg-card p-4 rounded-md flex flex-col min-h-[300px]">
        <Skeleton className="h-4 w-36 mb-3" />
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-40 w-40 rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap mt-2">
          {r(5).map((i) => (
            <Skeleton key={i} className="h-5 w-16 rounded-full" />
          ))}
        </div>
      </div>
      {/* Defect Status — doughnut + chips */}
      <div className="dashboard-card bg-card p-4 rounded-md flex flex-col min-h-[300px]">
        <Skeleton className="h-4 w-32 mb-3" />
        <div className="flex-1 flex items-center justify-center">
          <Skeleton className="h-40 w-40 rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap mt-2">
          {r(4).map((i) => (
            <Skeleton key={i} className="h-5 w-16 rounded-full" />
          ))}
        </div>
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   3. Defects  (/defects)
   Layout: Breadcrumb + 5 DefectDashboard stat cards + DefectManagement table
   ───────────────────────────────────────────────────────── */
/* Shared sub-component: left tree panel (w-80) used by both defect skeletons */
function DefectTreePanelSkeleton() {
  return (
    <div className="flex h-full w-80 shrink-0 flex-col border-r border-border bg-background">
      {/* Header h-14 */}
      <div className="flex h-14 shrink-0 items-center gap-2.5 border-b border-border px-4">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-4 w-16" />
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>
      {/* Search bar */}
      <div className="shrink-0 border-b border-border px-3 py-2.5">
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
      {/* Severity tabs */}
      <div className="shrink-0 border-b border-border px-3 py-2">
        <div className="flex gap-1">
          {["w-8", "w-12", "w-9", "w-14", "w-8"].map((w, i) => (
            <Skeleton key={i} className={`h-6 ${w} rounded-md`} />
          ))}
        </div>
      </div>
      {/* Status filter pills */}
      <div className="flex shrink-0 items-center gap-2.5 border-b border-border px-4 py-2">
        {["w-5", "w-9", "w-20", "w-9", "w-12", "w-12"].map((w, i) => (
          <Skeleton key={i} className={`h-2.5 ${w}`} />
        ))}
      </div>
      {/* Tree rows */}
      <div className="flex-1 overflow-hidden px-2 pt-2">
        <div className="flex items-center gap-2 px-2 py-1.5 mb-0.5">
          <Skeleton className="h-3 w-3 rounded-sm" />
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="ml-auto h-3.5 w-5 rounded-full" />
        </div>
        <div className="flex items-center gap-2 px-2 py-1.5 mb-0.5 pl-6">
          <Skeleton className="h-3 w-3 rounded-sm" />
          <Skeleton className="h-3.5 w-14" />
          <Skeleton className="ml-auto h-3.5 w-4 rounded-full" />
        </div>
        <div className="flex items-center gap-2 px-2 py-1.5 mb-0.5 pl-10">
          <Skeleton className="h-3 w-3 rounded-sm" />
          <Skeleton className="h-3.5 w-36" />
          <Skeleton className="ml-auto h-3.5 w-4 rounded-full" />
        </div>
        {r(6).map((i) => (
          <div key={i} className="flex items-center gap-2 px-2 py-2 pl-12">
            <Skeleton className="h-2 w-2 rounded-full" />
            <Skeleton className="h-3 flex-1" />
          </div>
        ))}
      </div>
      {/* Footer */}
      <div className="shrink-0 border-t border-border px-4 py-3">
        <Skeleton className="h-4 w-28" />
      </div>
    </div>
  );
}

export const DefectPageSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full overflow-hidden bg-background">
    <DefectTreePanelSkeleton />
    {/* Empty state placeholder */}
    <div className="flex flex-1 flex-col items-center justify-center gap-3 bg-background">
      <Skeleton className="h-12 w-12 rounded-full" />
      <Skeleton className="h-4 w-32" />
      <Skeleton className="h-3 w-64" />
      <Skeleton className="mt-2 h-8 w-32 rounded-md" />
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   4. Test Plans  (/test-management/test-plans)
   Layout: Breadcrumb + search + button + Card with SegmentedControl + plan cards grid
   ───────────────────────────────────────────────────────── */
export const CardGridSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton
      titleW="w-28"
      descW="w-96"
      actions={
        <>
          <Skeleton className="h-9 w-48 rounded-md" />
          <Skeleton className="h-9 w-28 rounded-md" />
        </>
      }
    />

    {/* Main card */}
    <div className="h-full min-h-0 flex flex-row">
      <div className="app-card flex-1 overflow-hidden flex flex-col h-full min-h-0">
        {/* SegmentedControl (Active Plans | Completed Plans) */}
        <div className="flex w-full rounded mx-4 mt-4">
          <Skeleton className="h-9 w-72 rounded-md" />
        </div>
        {/* Plan cards grid */}
        <div className="w-full flex-grow p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {r(4).map((i) => (
            <div
              key={i}
              className="border border-border rounded-md p-4 flex flex-col gap-3"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-5 w-16 rounded-full" />
              </div>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-3/4" />
              <div className="flex gap-4 mt-1">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   5. Plugins  (/settings/plugins)
   Layout: Breadcrumb + Card with 5-tab SegmentedControl + plugin card grid
   ───────────────────────────────────────────────────────── */
export const PluginsSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton titleW="w-44" descW="w-80" />

    {/* Main card */}
    <div className="h-full min-h-0 flex flex-row">
      <div className="app-card flex-1 overflow-hidden flex flex-col h-full min-h-0">
        {/* SegmentedControl (All | Bug Reporting | Test Lab | Product Mgmt | CI/CD) */}
        <div className="flex w-full rounded mx-4 mt-4">
          <Skeleton className="h-9 w-[480px] rounded-md" />
        </div>
        {/* Plugin cards grid */}
        <div className="w-full flex-grow p-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {r(6).map((i) => (
            <div
              key={i}
              className="border border-border rounded-md p-4 flex flex-col gap-3"
            >
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-md" />
                <Skeleton className="h-5 w-28" />
              </div>
              <Skeleton className="h-3.5 w-full" />
              <Skeleton className="h-3.5 w-3/4" />
              <div className="flex items-center justify-between mt-2">
                <Skeleton className="h-5 w-16 rounded-full" />
                <Skeleton className="h-8 w-20 rounded-md" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   6. Table Page  (Requirements, Test Suites, Test Runs, Reports, Test Data, Settings, etc.)
   Layout: Breadcrumb + button + app-card with GlobalTable
   ───────────────────────────────────────────────────────── */
export const TablePageSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton
      titleW="w-36"
      descW="w-64"
      actions={<Skeleton className="h-9 w-36 rounded-md" />}
    />

    <div className="h-full min-h-0 flex flex-row gap-4">
      <TableSurfaceSkeleton colCount={5} rowCount={10} withToolbar />
    </div>
  </div>
));

export const TablePageNoActionSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton titleW="w-36" descW="w-80" />
    <div className="h-full min-h-0 flex flex-row gap-4">
      <TableSurfaceSkeleton colCount={5} rowCount={10} withToolbar />
    </div>
  </div>
));

export const RequirementsSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full flex-row bg-background">
    {/* Left tree sidebar — mirrors RequirementFolderTree: icon + "Requirements"
        + New/AI buttons, search bar, type-filter pills, folder tree. */}
    <div className="flex w-[280px] shrink-0 flex-col overflow-hidden border-r border-border bg-background">
      {/* h-14 header */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-4 w-24" />
        <div className="ml-auto flex items-center gap-1.5">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>
      {/* Search bar */}
      <div className="shrink-0 border-b border-border px-3 py-2.5">
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
      {/* Type filter pills */}
      <div className="flex shrink-0 gap-1 border-b border-border px-3 pb-2 pt-2">
        <Skeleton className="h-5 w-10 rounded-md" />
        <Skeleton className="h-5 w-20 rounded-md" />
        <Skeleton className="h-5 w-24 rounded-md" />
        <Skeleton className="h-5 w-16 rounded-md" />
      </div>
      {/* Folder tree */}
      <div className="min-h-0 flex-1 overflow-hidden py-2">
        <FolderTreeSkeleton rows={9} />
      </div>
    </div>
    {/* Center — mirrors the "Select a requirement" empty state */}
    <div className="flex flex-1 flex-col items-center justify-center gap-2 bg-background px-8">
      <Skeleton className="h-12 w-12 rounded-full" />
      <Skeleton className="mt-1 h-4 w-40" />
      <Skeleton className="h-3 w-64" />
      <Skeleton className="h-3 w-52" />
      <div className="mt-3 flex items-center gap-2">
        <Skeleton className="h-8 w-36 rounded-md" />
        <Skeleton className="h-8 w-36 rounded-md" />
      </div>
    </div>
  </div>
));

export const TestSuiteSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton
      titleW="w-36"
      descW="w-96"
      actions={<Skeleton className="h-9 w-28 rounded-md" />}
    />
    <div className="flex-1 min-h-0 flex-row gap-4 flex">
      <TableSurfaceSkeleton colCount={5} rowCount={10} withToolbar />
    </div>
  </div>
));

export const TestDataSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton
      titleW="w-44"
      descW="w-72"
      actions={<Skeleton className="h-9 w-36 rounded-md" />}
    />
    <div className="h-full min-h-0 flex flex-row gap-4">
      <TableSurfaceSkeleton colCount={5} rowCount={10} withToolbar />
    </div>
  </div>
));

export const ReportsSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton titleW="w-36" descW="w-80" />
    <div className="h-full min-h-0 flex flex-row">
      <TableSurfaceSkeleton
        colCount={5}
        rowCount={10}
        withToolbar={false}
        withOuterPadding
      />
    </div>
  </div>
));

export const ProjectsSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full flex-row bg-background">
    {/* ── LEFT: Projects explorer (280px) ── */}
    <div className="hidden w-[280px] shrink-0 flex-col border-r border-border bg-background lg:flex">
      {/* h-14 header: black icon square + "Projects" + New project button */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-4 w-16" />
        </div>
        <Skeleton className="h-7 w-7 rounded-md" />
      </div>
      {/* ACTIVE PROJECT switcher */}
      <div className="shrink-0 border-b border-border px-2 py-2">
        <Skeleton className="mb-1.5 ml-1 h-2.5 w-20" />
        <div className="flex items-center gap-2 rounded-md border border-border px-2 py-1.5">
          <Skeleton className="h-6 w-6 rounded" />
          <Skeleton className="h-3.5 flex-1" />
          <Skeleton className="h-3.5 w-3.5 rounded" />
        </div>
      </div>
      {/* Search */}
      <div className="shrink-0 border-b border-border p-2">
        <Skeleton className="h-7 w-full rounded-md" />
      </div>
      {/* ALL PROJECTS group + rows */}
      <div className="min-h-0 flex-1 overflow-hidden p-1.5">
        <div className="flex items-center gap-1.5 px-2 py-1">
          <Skeleton className="h-3 w-3 rounded" />
          <Skeleton className="h-2.5 w-20" />
        </div>
        <div className="space-y-0.5">
          {r(9).map((i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1.5">
              <Skeleton className="h-3 w-3 rounded" />
              <Skeleton className="h-3.5 w-3.5 rounded" />
              <Skeleton className="h-3.5 flex-1" />
              <Skeleton className="ml-auto h-1.5 w-1.5 rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* ── CENTER: header + KPI strip + section nav + overview content ── */}
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
      {/* header (border-b px-6 pt-4 pb-3) */}
      <div className="shrink-0 border-b border-border px-6 pt-4 pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <Skeleton className="h-11 w-11 rounded-lg" />
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-4 w-14 rounded-md" />
                <Skeleton className="h-4 w-12 rounded-md" />
                <Skeleton className="h-4 w-16 rounded-md" />
              </div>
              <Skeleton className="h-3 w-44" />
              <div className="flex items-center gap-3">
                <Skeleton className="h-3 w-32" />
                <Skeleton className="h-3 w-20" />
              </div>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Skeleton className="h-9 w-9 rounded-full" />
            <div className="mx-1 h-8 w-px bg-border" />
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-8 w-32 rounded-md" />
          </div>
        </div>
      </div>
      {/* KPI strip — 12 cells (grid-cols-4 / xl:6 / 2xl:12) */}
      <div className="grid shrink-0 grid-cols-4 gap-px border-b border-border bg-border xl:grid-cols-6 2xl:grid-cols-12">
        {r(12).map((i) => (
          <div key={i} className="bg-background px-3 py-2.5">
            <Skeleton className="h-2.5 w-14" />
            <Skeleton className="mt-2 h-4 w-10" />
          </div>
        ))}
      </div>
      {/* Section nav — 8 tabs */}
      <div className="flex shrink-0 items-center gap-1 border-b border-border bg-card/40 px-4 py-1.5">
        {[16, 22, 14, 20, 20, 24, 16, 24].map((w, i) => (
          <Skeleton
            key={i}
            className="h-7 rounded-md"
            style={{ width: `${w * 4}px` }}
          />
        ))}
      </div>
      {/* Overview content — 2-col cards */}
      <div className="min-h-0 flex-1 overflow-hidden px-6 py-6">
        <div className="grid gap-5 xl:grid-cols-3">
          <div className="space-y-5 xl:col-span-2">
            <Skeleton className="h-64 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
          <div className="space-y-5">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>

    {/* ── RIGHT: context panel (300px, xl+) ── */}
    <aside className="hidden w-[300px] shrink-0 flex-col border-l border-border bg-background xl:flex">
      {/* h-12 header tabs: Project Context | Activity + status pill */}
      <div className="flex h-12 shrink-0 items-center justify-between gap-1 border-b border-border px-2">
        <div className="flex items-center gap-1">
          <Skeleton className="h-7 w-28 rounded-md" />
          <Skeleton className="h-7 w-20 rounded-md" />
        </div>
        <Skeleton className="h-5 w-14 rounded-full" />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {/* OWNERSHIP — 3 label/value rows */}
        <div className="border-b border-border px-4 py-4">
          <Skeleton className="mb-3 h-2.5 w-20" />
          {r(3).map((i) => (
            <div
              key={i}
              className="mb-2.5 flex items-center justify-between last:mb-0"
            >
              <Skeleton className="h-3.5 w-16" />
              <Skeleton className="h-3.5 w-28" />
            </div>
          ))}
        </div>
        {/* DELIVERY — 4 label/value rows */}
        <div className="border-b border-border px-4 py-4">
          <Skeleton className="mb-3 h-2.5 w-16" />
          {r(4).map((i) => (
            <div
              key={i}
              className="mb-2.5 flex items-center justify-between last:mb-0"
            >
              <Skeleton className="h-3.5 w-20" />
              <Skeleton className="h-3.5 w-16" />
            </div>
          ))}
        </div>
        {/* AI Status — bordered card */}
        <div className="border-b border-border px-4 py-4">
          <div className="rounded-lg border border-border p-3">
            <Skeleton className="mb-3 h-3.5 w-16" />
            {r(3).map((i) => (
              <div
                key={i}
                className="mb-2 flex items-center justify-between last:mb-0"
              >
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-14" />
              </div>
            ))}
          </div>
        </div>
        {/* CREATED & UPDATED */}
        <div className="px-4 py-4">
          <Skeleton className="mb-3 h-2.5 w-24" />
          {r(2).map((i) => (
            <div key={i} className="mb-3 flex items-center gap-2 last:mb-0">
              <Skeleton className="h-6 w-6 rounded-full" />
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-2.5 w-32" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </aside>
  </div>
));

export const UsersSkeleton = memo(() => (
  <div className="h-screen min-h-0 w-full flex flex-col gap-3 py-4">
    <PageHeaderSkeleton
      titleW="w-16"
      descW="w-56"
      actions={<Skeleton className="h-9 w-32 rounded-md" />}
    />
    <div className="h-full min-h-0 flex flex-row gap-4">
      <TableSurfaceSkeleton colCount={5} rowCount={10} withToolbar />
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   7. Test Case Result Page (/testcases/:id/test-results/:id)
   Layout: Header + [left result summary + steps table | right executed-step panel]
   ───────────────────────────────────────────────────────── */
export const TestCaseResultSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full">
    {/* LEFT — folder tree sidebar */}
    <div className="w-[280px] flex-shrink-0 overflow-hidden border-r border-border">
      <div className="px-3 py-3">
        <Skeleton className="h-7 w-full rounded-md" />
      </div>
      <FolderTreeSkeleton rows={10} />
    </div>

    {/* CENTER — header + run-results stats + steps */}
    <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
      {/* Header bar — back + breadcrumb + Re-Run, then meta badge pills */}
      <div className="border-b border-border px-6 pt-3 pb-2.5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Skeleton className="h-7 w-7 rounded-md flex-shrink-0" />
            <Skeleton className="h-4 w-16 flex-shrink-0" />
            <Skeleton className="h-1.5 w-1.5 rounded-full flex-shrink-0" />
            <Skeleton className="h-4 w-72" />
          </div>
          <Skeleton className="h-7 w-28 rounded-md flex-shrink-0" />
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <Skeleton className="h-4 w-20 rounded-md" />
          <Skeleton className="h-4 w-32 rounded-md" />
          <Skeleton className="h-4 w-20 rounded-md" />
        </div>
      </div>

      {/* Run Results — status row + description + 6 stat cards + actions */}
      <div className="border-b border-border px-5 py-3">
        <div className="flex items-center gap-2 mb-2">
          <Skeleton className="h-3.5 w-20" />
          <Skeleton className="h-4 w-16 rounded-full" />
          <Skeleton className="ml-auto h-5 w-24 rounded-md" />
        </div>
        <Skeleton className="h-3.5 w-80 mb-3" />

        <div className="grid grid-cols-6 gap-2">
          {r(6).map((i) => (
            <div
              key={i}
              className="rounded-lg border border-border/60 px-2.5 py-2"
            >
              <Skeleton className="h-2.5 w-12 mb-1.5" />
              <Skeleton className="h-5 w-8" />
            </div>
          ))}
        </div>

        <div className="mt-3 flex items-center justify-end gap-1.5">
          <Skeleton className="h-6 w-20 rounded" />
          <Skeleton className="h-6 w-16 rounded" />
          <Skeleton className="h-6 w-20 rounded" />
        </div>
      </div>

      {/* Steps table — number + status icon + text + duration */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {r(7).map((i) => (
          <div
            key={i}
            className="flex items-center gap-3 px-5 py-3.5 border-b border-border/50"
          >
            <Skeleton className="h-3.5 w-4 flex-shrink-0" />
            <Skeleton className="h-4 w-4 rounded-full flex-shrink-0" />
            <Skeleton
              className="h-3.5 flex-1"
              style={{ maxWidth: `${320 + ((i * 23) % 200)}px` }}
            />
            <Skeleton className="ml-auto h-3.5 w-14 flex-shrink-0" />
          </div>
        ))}
      </div>
    </div>

    {/* RIGHT — executed-step detail panel */}
    <div className="w-[350px] min-w-[300px] flex-shrink-0 overflow-hidden border-l border-border p-4">
      <div className="flex items-center justify-between mb-4">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-4 w-16 rounded-full" />
      </div>

      <Skeleton className="h-2.5 w-16 mb-1.5" />
      <Skeleton className="h-3.5 w-44 mb-4" />

      <Skeleton className="h-2.5 w-20 mb-1.5" />
      <Skeleton className="h-32 w-full rounded-md mb-4" />

      <Skeleton className="h-2.5 w-28 mb-2.5" />
      <div className="space-y-2.5">
        {r(5).map((i) => (
          <div key={i} className="flex items-center justify-between">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   8. Detail Page  (DefectView, TestPlan details, TestSuite details, etc.)
   Layout: Back breadcrumb + title + app-card with form fields
   ───────────────────────────────────────────────────────── */
export const DetailPageSkeleton = memo(() => (
  <div className="h-screen w-full flex flex-col gap-3 py-4">
    {/* Header with back */}
    <div className="flex items-center justify-between">
      <div className="flex flex-col gap-1">
        <Skeleton className="h-3.5 w-48" />
        <Skeleton className="h-6 w-72" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-9 w-20 rounded-md" />
        <Skeleton className="h-9 w-20 rounded-md" />
      </div>
    </div>
    {/* Content card */}
    <div className="app-card p-6 flex flex-col gap-5">
      {r(5).map((i) => (
        <div key={i} className="flex flex-col gap-2">
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      ))}
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   Defect detail content skeleton — shows only center + right panels
   (used when loading a defect after tree panel is already visible)
   ───────────────────────────────────────────────────────── */
export const DefectDetailContentSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full overflow-hidden bg-background">
    {/* ── Main panel (center) ────────────────────────────── */}
    <main className="flex-1 min-w-0 flex flex-col bg-background">
      {/* Header h-14: breadcrumb + title + buttons */}
      <div className="flex h-14 shrink-0 items-center gap-3 px-6 border-b border-border">
        <Skeleton className="h-4 w-4 rounded-sm" />
        <Skeleton className="h-3.5 w-16" />
        <Skeleton className="h-3 w-3 rounded-sm" />
        <Skeleton className="h-4 flex-1 max-w-sm" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>

      {/* Meta-chips row: severity + status + opened date + updated */}
      <div className="flex items-center gap-2 px-6 py-2 border-b border-border">
        <Skeleton className="h-6 w-14 rounded-md" />
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-36 rounded-md" />
        <Skeleton className="ml-auto h-3.5 w-28" />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-auto px-6 py-5">
        {/* Description card */}
        <div className="overflow-hidden rounded-md border border-border">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/30 px-3 py-2">
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-3.5 w-40" />
            </div>
            <Skeleton className="h-3 w-28" />
          </div>
          <div className="px-3 py-3 flex flex-col gap-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-5/6" />
            <Skeleton className="h-3.5 w-4/6" />
            <Skeleton className="h-3.5 w-3/6" />
          </div>
        </div>

        {/* Activity section */}
        <div className="mt-6 border-t border-border pt-5">
          <div className="flex items-baseline gap-2 mb-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-44" />
          </div>
          {r(2).map((i) => (
            <div
              key={i}
              className="mb-3 overflow-hidden rounded-md border border-border"
            >
              <div className="flex items-center gap-3 border-b border-border bg-muted/30 px-3 py-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="px-3 py-3">
                <Skeleton className="h-20 w-full rounded-md" />
              </div>
            </div>
          ))}
          {/* Comment input */}
          <div className="mt-4">
            <div className="flex items-center gap-3 border-b border-border pb-2 mb-2">
              <Skeleton className="h-5 w-10" />
              <Skeleton className="h-5 w-14" />
            </div>
            <Skeleton className="h-20 w-full rounded-md" />
          </div>
        </div>
      </div>
    </main>

    {/* ── Right aside w-[320px] ───────────────────────────── */}
    <aside className="flex h-full w-[320px] shrink-0 flex-col bg-background">
      {/* Scrollable sidebar content */}
      <div className="flex-1 overflow-y-auto">
        {/* Section 1: Status, Priority, Severity */}
        <div className="border-b border-border px-5 py-4">
          {[
            { label: "Status", w: "w-12" },
            { label: "Priority", w: "w-14" },
            { label: "Severity", w: "w-16" },
          ].map((item, i) => (
            <div key={i} className="space-y-3">
              <div>
                <Skeleton className={`h-2.5 ${item.w} mb-1.5`} />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 2: Type, Environment */}
        <div className="border-b border-border px-5 py-4">
          {[
            { label: "Type", w: "w-10" },
            { label: "Environment", w: "w-20" },
          ].map((item, i) => (
            <div key={i} className="space-y-3">
              <div>
                <Skeleton className={`h-2.5 ${item.w} mb-1.5`} />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 3: Assignee, Reviewer, Reporter */}
        <div className="border-b border-border px-5 py-4">
          {[
            { label: "Assignee", w: "w-16" },
            { label: "Reviewer", w: "w-16" },
            { label: "Reporter", w: "w-16" },
          ].map((item, i) => (
            <div key={i} className="space-y-3">
              <div>
                <Skeleton className={`h-2.5 ${item.w} mb-1.5`} />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 4: Test Cycles, Linked Test Cases, Referenced */}
        <div className="border-b border-border px-5 py-4">
          {/* Test Cycles */}
          <div className="mb-4">
            <Skeleton className="h-2.5 w-20 mb-1.5" />
            <div className="flex flex-wrap gap-1.5">
              <Skeleton className="h-6 w-24 rounded-md" />
              <Skeleton className="h-6 w-20 rounded-md" />
            </div>
          </div>

          {/* Linked Test Cases */}
          <div className="mb-4">
            <Skeleton className="h-2.5 w-28 mb-1.5" />
            <div className="flex flex-wrap gap-1.5">
              <Skeleton className="h-6 w-24 rounded-md" />
              <Skeleton className="h-6 w-20 rounded-md" />
            </div>
          </div>

          {/* Referenced */}
          <div>
            <Skeleton className="h-2.5 w-20 mb-1.5" />
            <div className="flex flex-wrap gap-1.5">
              <Skeleton className="h-6 w-20 rounded-md" />
              <Skeleton className="h-6 w-20 rounded-md" />
            </div>
          </div>
        </div>

        {/* Section 5: Created & Updated */}
        <div className="border-b border-border px-5 py-4 last:border-b-0">
          <div className="space-y-3">
            {/* Created by */}
            <div>
              <Skeleton className="h-2.5 w-16 mb-1.5" />
              <div className="flex items-center gap-2 mb-1">
                <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>

            {/* Last updated by */}
            <div>
              <Skeleton className="h-2.5 w-20 mb-1.5" />
              <div className="flex items-center gap-2 mb-1">
                <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  </div>
));

/* ─────────────────────────────────────────────────────────
   Defect detail panel skeleton — mirrors DefectDetailPanel.tsx:
    • Main (flex-1): h-14 header (icon + mono key + › + title + buttons),
      meta-chips row (severity pill, status pill, opened date, updated),
      description card (avatar + name row + body), activity section.
    • Right aside (w-[320px]): h-14 header with Details|Comments tabs,
      then 8 sidebar fields (TYPE … REPORTER) + Linked Test Cases.
   ───────────────────────────────────────────────────────── */
export const DefectDetailSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full overflow-hidden bg-background">
    {/* ── Left tree panel ────────────────────────────────── */}
    <DefectTreePanelSkeleton />

    {/* ── Main panel (center) ────────────────────────────── */}
    <main className="flex-1 min-w-0 flex flex-col bg-background">
      {/* Header h-14: breadcrumb + title + buttons */}
      <div className="flex h-14 shrink-0 items-center gap-3 px-6 border-b border-border">
        <Skeleton className="h-4 w-4 rounded-sm" />
        <Skeleton className="h-3.5 w-16" />
        <Skeleton className="h-3 w-3 rounded-sm" />
        <Skeleton className="h-4 flex-1 max-w-sm" />
        <div className="ml-auto flex items-center gap-2">
          <Skeleton className="h-9 w-28 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-7 w-7 rounded-md" />
        </div>
      </div>

      {/* Meta-chips row: severity + status + opened date + updated */}
      <div className="flex items-center gap-2 px-6 py-2 border-b border-border">
        <Skeleton className="h-6 w-14 rounded-md" />
        <Skeleton className="h-6 w-16 rounded-full" />
        <Skeleton className="h-6 w-36 rounded-md" />
        <Skeleton className="ml-auto h-3.5 w-28" />
      </div>

      {/* Scrollable content area */}
      <div className="flex-1 overflow-auto px-6 py-5">
        {/* Description card */}
        <div className="overflow-hidden rounded-md border border-border">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/30 px-3 py-2">
            <div className="flex items-center gap-2.5">
              <Skeleton className="h-6 w-6 rounded-full" />
              <Skeleton className="h-3.5 w-40" />
            </div>
            <Skeleton className="h-3 w-28" />
          </div>
          <div className="px-3 py-3 flex flex-col gap-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-5/6" />
            <Skeleton className="h-3.5 w-4/6" />
            <Skeleton className="h-3.5 w-3/6" />
          </div>
        </div>

        {/* Activity section */}
        <div className="mt-6 border-t border-border pt-5">
          <div className="flex items-baseline gap-2 mb-4">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-3 w-44" />
          </div>
          {r(2).map((i) => (
            <div
              key={i}
              className="mb-3 overflow-hidden rounded-md border border-border"
            >
              <div className="flex items-center gap-3 border-b border-border bg-muted/30 px-3 py-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-3.5 w-28" />
                <Skeleton className="h-3 w-16" />
              </div>
              <div className="px-3 py-3">
                <Skeleton className="h-20 w-full rounded-md" />
              </div>
            </div>
          ))}
          {/* Comment input */}
          <div className="mt-4">
            <div className="flex items-center gap-3 border-b border-border pb-2 mb-2">
              <Skeleton className="h-5 w-10" />
              <Skeleton className="h-5 w-14" />
            </div>
            <Skeleton className="h-20 w-full rounded-md" />
          </div>
        </div>
      </div>
    </main>

    {/* ── Right aside w-[320px] ───────────────────────────── */}
    <aside className="flex h-full w-[320px] shrink-0 flex-col border-l border-border bg-background">
      {/* Scrollable sidebar content */}
      <div className="flex-1 overflow-y-auto">
        {/* Section 1: Status, Priority, Severity */}
        <div className="border-b border-border px-5 py-4">
          {[
            { label: "Status", w: "w-12" },
            { label: "Priority", w: "w-14" },
            { label: "Severity", w: "w-16" },
          ].map((item, i) => (
            <div key={i} className="space-y-3">
              <div>
                <Skeleton className={`h-2.5 ${item.w} mb-1.5`} />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 2: Type, Environment */}
        <div className="border-b border-border px-5 py-4">
          {[
            { label: "Type", w: "w-10" },
            { label: "Environment", w: "w-20" },
          ].map((item, i) => (
            <div key={i} className="space-y-3">
              <div>
                <Skeleton className={`h-2.5 ${item.w} mb-1.5`} />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 3: Assignee, Reviewer, Reporter */}
        <div className="border-b border-border px-5 py-4">
          {[
            { label: "Assignee", w: "w-16" },
            { label: "Reviewer", w: "w-16" },
            { label: "Reporter", w: "w-16" },
          ].map((item, i) => (
            <div key={i} className="space-y-3">
              <div>
                <Skeleton className={`h-2.5 ${item.w} mb-1.5`} />
                <Skeleton className="h-9 w-full rounded-md" />
              </div>
            </div>
          ))}
        </div>

        {/* Section 4: Test Cycles, Linked Test Cases, Referenced */}
        <div className="border-b border-border px-5 py-4">
          {/* Test Cycles */}
          <div className="mb-4">
            <Skeleton className="h-2.5 w-20 mb-1.5" />
            <div className="flex flex-wrap gap-1.5">
              <Skeleton className="h-6 w-24 rounded-md" />
              <Skeleton className="h-6 w-20 rounded-md" />
            </div>
          </div>

          {/* Linked Test Cases */}
          <div className="mb-4">
            <Skeleton className="h-2.5 w-28 mb-1.5" />
            <div className="flex flex-wrap gap-1.5">
              <Skeleton className="h-6 w-24 rounded-md" />
              <Skeleton className="h-6 w-20 rounded-md" />
            </div>
          </div>

          {/* Referenced */}
          <div>
            <Skeleton className="h-2.5 w-20 mb-1.5" />
            <div className="flex flex-wrap gap-1.5">
              <Skeleton className="h-6 w-20 rounded-md" />
              <Skeleton className="h-6 w-20 rounded-md" />
            </div>
          </div>
        </div>

        {/* Section 5: Created & Updated */}
        <div className="border-b border-border px-5 py-4 last:border-b-0">
          <div className="space-y-3">
            {/* Created by */}
            <div>
              <Skeleton className="h-2.5 w-16 mb-1.5" />
              <div className="flex items-center gap-2 mb-1">
                <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>

            {/* Last updated by */}
            <div>
              <Skeleton className="h-2.5 w-20 mb-1.5" />
              <div className="flex items-center gap-2 mb-1">
                <Skeleton className="h-6 w-6 rounded-full flex-shrink-0" />
                <Skeleton className="h-4 w-24" />
              </div>
              <div className="flex items-center gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
                <Skeleton className="h-3 w-32" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </aside>
  </div>
));

/* ─────────────────────────────────────────────────────────
   Element panel — Select Element list.
   Mirrors SelectElement's three-column table (ELEMENT NAME | SCREEN NAME | STATUS):
   each row is a flex row with flex-[2], flex-[1.5], flex-[1] cells (px-3 py-2.5). Renders the
   header row + skeleton body rows so a load shows the exact table structure.
   ───────────────────────────────────────────────────────── */
export const ElementListSkeleton = memo(({ rows = 9 }: { rows?: number }) => (
  <div className="flex flex-col">
    {r(rows).map((i) => (
      <div
        key={i}
        className="flex flex-row items-center border-b border-border/60 px-3 py-2"
      >
        <span className="flex-[2] min-w-0 pr-2">
          <Skeleton
            className="h-3.5"
            style={{ width: `${55 + ((i * 17) % 35)}%` }}
          />
        </span>
        <span className="flex-[1.5] min-w-0 pr-2">
          <Skeleton
            className="h-3.5"
            style={{ width: `${40 + ((i * 13) % 35)}%` }}
          />
        </span>
        <span className="flex-[1] min-w-0">
          <Skeleton className="h-4 w-16 rounded-full" />
        </span>
      </div>
    ))}
  </div>
));

/* ─────────────────────────────────────────────────────────
   Elements page — full 3-pane shell (route: /elements).
   Mirrors src/pages/scenarios/elements/index.tsx:
     • Left Screens tree sidebar (w-[280px]): h-14 header (icon box + "Screens"
       + count), search box, "All" filter row, then folder tree rows.
     • Center: h-14 header (folder breadcrumb + Bulk record + Create element),
       filter-chips row (All/XPATH/CSS Selector/ID/Link Text/Class Name), and the
       4-column element table (Name | Locator Type | Locator Value | Affected).
   ───────────────────────────────────────────────────────── */
export const ElementsPageSkeleton = memo(() => {
  const hasElement =
    typeof window !== "undefined" &&
    window.location.search.includes("elementId=");
  return (
    <div className="flex h-full min-h-0 w-full flex-row">
      {/* Left: Screens tree sidebar */}
      <div className="flex h-full w-[280px] shrink-0 flex-col border-r border-border bg-background">
        {/* Header */}
        <div className="flex h-14 items-center gap-2 border-b border-border px-4">
          <Skeleton className="h-7 w-7 rounded-md" />
          <Skeleton className="h-4 w-16" />
          <Skeleton className="ml-auto h-3 w-5" />
        </div>
        {/* Search */}
        <div className="border-b border-border px-3 py-2.5">
          <Skeleton className="h-8 w-full rounded-md" />
        </div>
        {/* Filter pills */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-2">
          <Skeleton className="h-3.5 w-8" />
          <Skeleton className="h-3.5 w-12" />
          <Skeleton className="h-3.5 w-16" />
        </div>
        {/* Tree rows */}
        <div className="px-2 py-2">
          <FolderTreeSkeleton rows={9} />
        </div>
      </div>

      {/* Center: element table */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
        {/* Header */}
        <div className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-6">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-3.5 w-40" />
          <div className="ml-auto flex items-center gap-2">
            <Skeleton className="h-8 w-28 rounded-md" />
            <Skeleton className="h-8 w-32 rounded-md" />
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          {[40, 56, 80, 32, 64, 72].map((w, i) => (
            <Skeleton
              key={i}
              className="h-5 rounded-md"
              style={{ width: `${w}px` }}
            />
          ))}
        </div>

        {/* Table header */}
        <div className="flex items-center border-b border-border px-6 py-2.5">
          <span className="basis-[34%]">
            <Skeleton className="h-3 w-14" />
          </span>
          <span className="basis-[18%]">
            <Skeleton className="h-3 w-20" />
          </span>
          <span className="basis-[38%]">
            <Skeleton className="h-3 w-20" />
          </span>
          <span className="basis-[10%]">
            <Skeleton className="h-3 w-12" />
          </span>
        </div>

        {/* Table rows */}
        <div className="flex-1 overflow-hidden">
          {r(11).map((i) => (
            <div
              key={i}
              className="flex items-center border-b border-border px-6 py-3"
            >
              <span className="basis-[34%]">
                <Skeleton
                  className="h-3.5"
                  style={{ width: `${45 + ((i * 13) % 35)}%` }}
                />
                <Skeleton
                  className="mt-1 h-2.5"
                  style={{ width: `${30 + ((i * 17) % 30)}%` }}
                />
              </span>
              <span className="basis-[18%]">
                <Skeleton className="h-5 w-16 rounded-md" />
              </span>
              <span className="basis-[38%]">
                <Skeleton
                  className="h-3.5"
                  style={{ width: `${50 + ((i * 11) % 40)}%` }}
                />
              </span>
              <span className="basis-[10%]">
                <Skeleton className="h-7 w-7 rounded-md" />
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Right: Element detail panel */}
      {hasElement && (
        <div className="flex h-full w-[336px] shrink-0 flex-col border-l border-border bg-background">
          <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
            <div className="flex gap-2">
              <Skeleton className="h-6 w-16 rounded-md" />
              <Skeleton className="h-6 w-16 rounded-md" />
            </div>
            <Skeleton className="h-6 w-6 rounded-md" />
          </div>
          <div className="flex-1 overflow-hidden p-5">
            <div className="mb-6 space-y-3">
              <Skeleton className="h-3 w-12" />
              <Skeleton className="h-8 w-full rounded-md" />
            </div>
            <div className="mb-6 space-y-3">
              <Skeleton className="h-3 w-16" />
              <Skeleton className="h-10 w-full rounded-md" />
            </div>
            <div className="mb-6 space-y-3">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-20 w-full rounded-md" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

/* ─────────────────────────────────────────────────────────
   Uploads page — full 3-pane shell (route: /uploads).
   Mirrors src/pages/uploads/index.tsx:
     • Left Files sidebar (w-[280px]): h-14 header (icon box + "Files" + add
       button), search box, segmented filter (All/Image/Docs), secondary filter
       chips row (Data/Video/Archive), file-row list, bottom "Upload file" bar.
     • Center: h-14 header (Dashboard › Files breadcrumb + Download/Upload), empty
       preview body.
     • Right Details panel (w-[320px], xl only): "Details" tab + property rows.
   ───────────────────────────────────────────────────────── */
export const UploadsPageSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full overflow-hidden bg-background">
    {/* Left: Files sidebar */}
    <aside className="flex w-[280px] shrink-0 flex-col border-r border-border bg-background">
      {/* Header */}
      <div className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
        <Skeleton className="h-7 w-7 rounded-md" />
        <Skeleton className="h-4 w-12" />
        <Skeleton className="ml-auto h-7 w-7 rounded-md" />
      </div>
      {/* Search */}
      <div className="shrink-0 border-b border-border px-3 py-2.5">
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
      {/* Segmented filter (All / Image / Docs) */}
      <div className="shrink-0 border-b border-border px-3 py-2">
        <Skeleton className="h-8 w-full rounded-md" />
      </div>
      {/* Secondary filter chips (Data / Video / Archive) */}
      <div className="flex shrink-0 items-center gap-3 border-b border-border px-4 py-2">
        <Skeleton className="h-3.5 w-12" />
        <Skeleton className="h-3.5 w-14" />
        <Skeleton className="h-3.5 w-16" />
        <Skeleton className="ml-auto h-3.5 w-3.5 rounded" />
      </div>
      {/* File rows */}
      <div className="flex-1 overflow-hidden">
        {r(7).map((i) => (
          <div
            key={i}
            className="flex items-start gap-2 border-b border-border px-3 py-2"
          >
            <Skeleton className="mt-0.5 h-7 w-7 shrink-0 rounded-md" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Skeleton
                  className="h-3.5"
                  style={{ width: `${45 + ((i * 13) % 35)}%` }}
                />
                <Skeleton className="h-3.5 w-8 rounded-sm" />
              </div>
              <Skeleton
                className="mt-1.5 h-2.5"
                style={{ width: `${55 + ((i * 11) % 30)}%` }}
              />
            </div>
          </div>
        ))}
      </div>
      {/* Bottom upload bar */}
      <div className="flex shrink-0 items-center gap-2 border-t border-border px-4 py-3">
        <Skeleton className="h-3.5 w-3.5 rounded" />
        <Skeleton className="h-3.5 w-20" />
      </div>
    </aside>

    {/* Center: preview area */}
    <section className="flex min-w-0 flex-1 flex-col border-r border-border">
      {/* Header */}
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-6">
        <Skeleton className="h-3.5 w-32" />
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-24 rounded-md" />
        </div>
      </div>
      {/* Empty preview body */}
      <div className="flex-1" />
    </section>

    {/* Right: Details panel */}
    <aside className="hidden w-[320px] shrink-0 flex-col border-l border-border bg-background xl:flex">
      <div className="flex shrink-0 items-center gap-0.5 border-b border-border px-2 py-1.5">
        <Skeleton className="h-7 w-20 rounded-md" />
      </div>
      <div className="flex-1" />
    </aside>
  </div>
));

/* ─────────────────────────────────────────────────────────
   Element panel — Update/New Element form.
   Mirrors ElementForm: top action row (Inspect button + info icon), the muted
   warning banner, then the fields (Name full-width, Screen Name + Element Type
   side-by-side, Element Value full-width, nested-context toggle).
   ───────────────────────────────────────────────────────── */
export const ElementFormSkeleton = memo(() => (
  <div className="w-full h-full py-2">
    {/* Action row: Record Element button + info icon */}
    <div className="flex items-center gap-4 px-6 mb-6">
      <Skeleton className="h-8 w-36 rounded-md" />
      <Skeleton className="h-8 w-8 rounded-md" />
    </div>

    {/* Warning banner */}
    <div className="mx-6 mb-4">
      <Skeleton className="h-12 w-full rounded-sm" />
    </div>

    {/* Fields */}
    <div className="flex flex-col gap-4 px-6">
      {/* Name */}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3.5 w-16" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>

      {/* Screen Name + Element Type (side by side) */}
      <div className="flex gap-4">
        <div className="flex flex-col gap-1.5" style={{ width: "50%" }}>
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
        <div className="flex flex-col gap-1.5" style={{ width: "50%" }}>
          <Skeleton className="h-3.5 w-24" />
          <Skeleton className="h-9 w-full rounded-md" />
        </div>
      </div>

      {/* Element Value */}
      <div className="flex flex-col gap-1.5">
        <Skeleton className="h-3.5 w-28" />
        <Skeleton className="h-9 w-full rounded-md" />
      </div>

      {/* Nested context checkbox */}
      <div className="flex items-center gap-2 pt-1">
        <Skeleton className="h-4 w-4 rounded" />
        <Skeleton className="h-3.5 w-48" />
      </div>

      {/* Screenshot area */}
      <div className="mt-2">
        <Skeleton className="h-32 w-full rounded-md" />
      </div>

      {/* Element Details section */}
      <div className="flex flex-col gap-1.5 mt-2">
        <Skeleton className="h-4 w-28" />
        <Skeleton className="h-3.5 w-40" />
      </div>
    </div>
  </div>
));

/* ─────────────────────────────────────────────────────────
   Guide Me  (/guideme)
   Layout: [Library sidebar 240px | Content | Tour details 340px]
   Center: search header + 3 summary cards + Recommended chips +
   Getting Started path + All-tours card grid.
   ───────────────────────────────────────────────────────── */
export const GuideMeSkeleton = memo(() => (
  <div className="flex h-full min-h-0 w-full flex-row overflow-hidden bg-background">
    {/* LEFT — Library sidebar */}
    <aside className="flex w-[240px] shrink-0 flex-col border-r border-border bg-background">
      {/* Brand header */}
      <div className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
        <Skeleton className="h-6 w-6 rounded-md" />
        <Skeleton className="h-4 w-24" />
      </div>

      {/* Nav */}
      <div className="flex-1 overflow-hidden px-2 py-3">
        {/* Library label */}
        <Skeleton className="mb-2 ml-2 h-2.5 w-14" />
        {r(2).map((i) => (
          <div key={i} className="flex items-center gap-2.5 px-2 py-1.5">
            <Skeleton className="h-3.5 w-3.5 rounded" />
            <Skeleton className="h-3.5 flex-1" />
            <Skeleton className="h-3 w-6" />
          </div>
        ))}

        {/* Categories label */}
        <Skeleton className="mb-2 ml-2 mt-4 h-2.5 w-20" />
        <div className="space-y-0.5">
          {r(6).map((i) => (
            <div key={i} className="flex items-center gap-2.5 px-2 py-1.5">
              <Skeleton className="h-3.5 w-3.5 rounded" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-3 w-6" />
                </div>
                <Skeleton className="mt-1.5 h-[3px] w-full rounded-full" />
              </div>
            </div>
          ))}
        </div>

        {/* Paths label */}
        <Skeleton className="mb-2 ml-2 mt-4 h-2.5 w-12" />
        {r(3).map((i) => (
          <div key={i} className="flex items-center gap-2.5 px-2 py-1.5">
            <Skeleton className="h-3.5 w-3.5 rounded" />
            <Skeleton className="h-3.5 flex-1" />
            <Skeleton className="h-3 w-10" />
          </div>
        ))}
      </div>

      {/* Progress card pinned bottom */}
      <div className="border-t border-border p-3">
        <div className="rounded-lg border border-border bg-card p-3">
          <div className="flex items-center justify-between">
            <Skeleton className="h-2.5 w-20" />
            <Skeleton className="h-3.5 w-3.5 rounded" />
          </div>
          <Skeleton className="mt-2 h-5 w-12" />
          <Skeleton className="mt-2 h-1.5 w-full rounded-full" />
          <div className="mt-2 flex items-center justify-between">
            <Skeleton className="h-2.5 w-16" />
            <Skeleton className="h-2.5 w-20" />
          </div>
        </div>
      </div>
    </aside>

    {/* CENTER — Content */}
    <section className="flex min-w-0 flex-1 flex-col overflow-hidden bg-background">
      {/* Search header */}
      <header className="flex h-12 shrink-0 items-center gap-3 border-b border-border px-6">
        <Skeleton className="h-8 w-full max-w-[520px] rounded-md" />
        <Skeleton className="ml-auto h-8 w-28 rounded-md" />
      </header>

      <div className="flex-1 overflow-hidden">
        <div className="mx-auto w-full max-w-[1200px] px-6 py-5">
          {/* Summary + Next up — 3 cards */}
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
            {r(3).map((i) => (
              <div
                key={i}
                className="rounded-xl border border-border bg-card p-4"
              >
                <div className="flex items-center justify-between">
                  <Skeleton className="h-2.5 w-24" />
                  <Skeleton className="h-3.5 w-3.5 rounded" />
                </div>
                <Skeleton className="mt-2.5 h-6 w-16" />
                <Skeleton className="mt-2 h-3 w-32" />
                <Skeleton className="mt-3 h-1.5 w-full rounded-full" />
              </div>
            ))}
          </div>

          {/* Recommended */}
          <div className="mt-6">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="mt-1.5 h-3 w-64" />
            <div className="mt-3 flex flex-wrap gap-2">
              {[130, 150, 120, 160, 110].map((w, i) => (
                <Skeleton
                  key={i}
                  className="h-8 rounded-full"
                  style={{ width: `${w}px` }}
                />
              ))}
            </div>
          </div>

          {/* Getting Started path */}
          <div className="mt-6">
            <div className="flex items-end justify-between">
              <div>
                <Skeleton className="h-4 w-44" />
                <Skeleton className="mt-1.5 h-3 w-72" />
              </div>
              <Skeleton className="h-3.5 w-24" />
            </div>
            <div className="mt-3 flex items-stretch gap-2 overflow-hidden">
              {r(5).map((i) => (
                <div
                  key={i}
                  className="flex w-[180px] shrink-0 flex-col rounded-lg border border-border bg-card px-3 py-2.5"
                >
                  <div className="flex items-center gap-1.5">
                    <Skeleton className="h-5 w-5 rounded-full" />
                    <Skeleton className="h-2.5 w-12" />
                  </div>
                  <Skeleton className="mt-1.5 h-3.5 w-28" />
                  <Skeleton className="mt-1.5 h-2.5 w-20" />
                </div>
              ))}
            </div>
          </div>

          {/* All tours grid */}
          <div className="mt-6 pb-8">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="mt-1.5 h-3 w-28" />
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
              {r(8).map((i) => (
                <div
                  key={i}
                  className="flex flex-col rounded-xl border border-border bg-card p-3.5"
                >
                  <div className="flex items-start justify-between gap-2">
                    <Skeleton className="h-8 w-8 rounded-lg" />
                    <Skeleton className="h-4 w-14 rounded-full" />
                  </div>
                  <Skeleton className="mt-2.5 h-3.5 w-32" />
                  <Skeleton className="mt-1.5 h-3 w-full" />
                  <Skeleton className="mt-1 h-3 w-3/4" />
                  <div className="mt-2.5 flex items-center gap-2">
                    <Skeleton className="h-2.5 w-10" />
                    <Skeleton className="h-2.5 w-8" />
                    <Skeleton className="h-2.5 w-16" />
                  </div>
                  <Skeleton className="mt-2.5 h-1 w-full rounded-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* RIGHT — Tour details */}
    <aside className="hidden w-[340px] shrink-0 flex-col border-l border-border bg-background lg:flex">
      {/* Header */}
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-border px-5">
        <Skeleton className="h-2.5 w-20" />
        <Skeleton className="h-2.5 w-16" />
      </div>

      <div className="flex-1 overflow-hidden">
        <div className="px-5 pt-5">
          {/* Icon + title + description */}
          <div className="flex items-start gap-3">
            <Skeleton className="h-10 w-10 rounded-lg" />
            <div className="min-w-0 flex-1">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="mt-2 h-3 w-full" />
              <Skeleton className="mt-1 h-3 w-3/4" />
            </div>
          </div>

          {/* Meta boxes */}
          <div className="mt-4 grid grid-cols-3 gap-2">
            {r(3).map((i) => (
              <div
                key={i}
                className="rounded-lg border border-border bg-card p-2.5"
              >
                <Skeleton className="h-2.5 w-12" />
                <Skeleton className="mt-2 h-4 w-10" />
              </div>
            ))}
          </div>

          {/* Primary + secondary buttons */}
          <Skeleton className="mt-4 h-9 w-full rounded-md" />
          <Skeleton className="mt-2 h-8 w-full rounded-md" />
        </div>

        {/* What you'll learn */}
        <div className="mt-5 border-t border-border px-5 py-4">
          <Skeleton className="h-2.5 w-24" />
          <div className="mt-2 space-y-1.5">
            {r(3).map((i) => (
              <div key={i} className="flex items-start gap-2">
                <Skeleton className="mt-0.5 h-3 w-3 rounded-full" />
                <Skeleton className="h-3 flex-1" />
              </div>
            ))}
          </div>
        </div>

        {/* Steps preview */}
        <div className="border-t border-border px-5 py-4">
          <div className="flex items-center justify-between">
            <Skeleton className="h-2.5 w-12" />
            <Skeleton className="h-2.5 w-12" />
          </div>
          <div className="mt-2 space-y-1.5">
            {r(4).map((i) => (
              <div key={i} className="flex items-start gap-2.5 px-1 py-1">
                <Skeleton className="h-5 w-5 shrink-0 rounded-full" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="h-3 w-36" />
                  <Skeleton className="mt-1 h-2.5 w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </aside>
  </div>
));
